import { useState } from 'react';
import type { SendRecord } from '../types';

interface RecordTableProps {
  records: SendRecord[];
}

export default function RecordTable({ records }: RecordTableProps) {
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const getMethodText = (method: string) => {
    return method === 'sms' ? '短信通知' : '邮箱通知';
  };

  const getStatusText = (status: string) => {
    return status === 'success' ? '成功' : '失败';
  };

  const truncateContent = (content: string, maxLength: number = 100) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  const toggleExpand = (id: number) => {
    const newExpanded = new Set(expandedIds);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedIds(newExpanded);
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">消息ID</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知事项</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知内容</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知方式</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">用户名称</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">手机号</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">电子邮箱</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">通知状态</th>
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900 whitespace-nowrap">发送时间</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {records.map((record) => {
              const isExpanded = expandedIds.has(record.id);
              const needsExpand = record.content.length > 100;

              return (
                <tr key={record.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.id}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.notificationItem}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-md">
                    <div>
                      {isExpanded ? record.content : truncateContent(record.content)}
                      {needsExpand && (
                        <button
                          onClick={() => toggleExpand(record.id)}
                          className="ml-2 text-teal-600 hover:text-teal-700 cursor-pointer whitespace-nowrap"
                        >
                          {isExpanded ? '收起' : '展开'}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 text-xs rounded-full whitespace-nowrap ${
                      record.method === 'sms'
                        ? 'bg-blue-50 text-blue-600'
                        : 'bg-purple-50 text-purple-600'
                    }`}>
                      {getMethodText(record.method)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.userName}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.phone}</td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 text-xs rounded-full whitespace-nowrap ${
                      record.status === 'success'
                        ? 'bg-green-50 text-green-600'
                        : 'bg-red-50 text-red-600'
                    }`}>
                      {getStatusText(record.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap">{record.sendTime}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
